import type { InjectionKey } from 'vue'

// A column of UiDataTable. `key` identifies it in the saved layout and, by default, is the field read
// from the row and the key sent to the API to sort (`sortKey` when they differ).
export interface DataTableColumn<Row = unknown> {
  key: string
  label: string
  // Header text read only by screen readers (icons, actions...).
  srOnlyLabel?: boolean
  sortable?: boolean
  sortKey?: string
  // Value used to sort on the client (default: row[key]).
  sortValue?: (row: Row) => unknown
  // Text of the cell when there's no `cell-<key>` slot (default: row[key], '—' when empty).
  format?: (row: Row) => string | number | null | undefined
  // The person can't hide it (the main column of the table).
  hideable?: boolean
  defaultHidden?: boolean
  align?: 'start' | 'end'
  nowrap?: boolean
  strong?: boolean
  numeric?: boolean
  cellClass?: string
  width?: string
}

export interface SortCriterion {
  key: string
  desc: boolean
}

// Columns order and the hidden ones, saved per person (in the database, through the app's store).
export interface TableLayout {
  order: string[]
  hidden: string[]
  // `layoutVersion` of the table when it was saved (missing = 1). Another version is ignored.
  version?: number
}

// What the app provides so tables keep the layout across browsers. Without it, the layout lives in memory.
export interface TablePreferencesStore {
  // Where the preferences belong (the institution, or the console); also separates the session sort.
  scope: () => string
  // Reactive read of the saved layout (undefined = default).
  layout: (tableId: string) => TableLayout | undefined
  // null = back to the default.
  save: (tableId: string, layout: TableLayout | null) => void
}

export const TABLE_PREFERENCES: InjectionKey<TablePreferencesStore> = Symbol('table-preferences')
